import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../components/context/Auth";
import styles from "./ProductionDetails.module.css";
import TopTabBar from "../../components/updatedComponents/Tanolet/TopTabBar";
import { formatDateTime, LastActionCell } from "../../Utils/Utils";
import ProductionFilters from "../../components/updatedComponents/Production/ProductionFilters";
import Pagination2 from "../../components/updatedComponents/FormComponents/Pagination2";
import { UpdateDrawer } from "../../components/updatedComponents/Production/ProductionDetails/UpdateDrawer";
import { ProgressBar } from "../../components/updatedComponents/Production/ProgressBar";
import ThreeDotMenu from "../../components/updatedComponents/Production/ThreeDotMenu";
import QueueEditModal from "../../components/updatedComponents/Production/QueueEditModal";
import { useToast } from "../../components/updatedComponents/Toaster/Toaster";
import TextElement from "../../components/updatedComponents/FormComponents/TextElement";
import Button from "../../components/updatedComponents/Dealers/Button";
import { CancelIcon2, LeftArrow } from "../../components/icons/Icon";
import { TemplateDone } from "../../components/icons/Icon2";
import { ModalWrapper } from "../../exports";
import AcceptProductionModal from "../../components/updatedComponents/Production/ProductionDetails/AcceptProductionModal";
import fetchData from "../../APIsControll/apiControll";
import CancelReasonModal from "../../components/updatedComponents/Production/CancelReasonModal";
import { CryptoDecrypt } from "../../Utils/TextUtils";
import ReleaseLineModal from "../../components/updatedComponents/Production/ReleaseLineModal";

// ─────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────

const TABLE_HEADERS = [
  "Queue",
  "Item No",
  "Product",
  "Required Qty",
  "Produced Qty",
  "Pending Qty",
  "Warehouse",
  "Production Line",
  "Progress",
  "Status",
  "Last Action",
];

const GRID_COLUMNS =
  "0.7fr 1fr 1.2fr 0.9fr 0.9fr 0.9fr 1fr 1.1fr 1fr 1.3fr 1.3fr 0.8fr";

const KEY_TO_FILTER = {
  "Total Items": "Total Items",
  "Total Qty": "Total Qty",

  "Pending Items": "Pending Items",
  "Pending Qty": "Pending Qty",

  "Completed Items": "Completed Items",
  "Completed Qty": "Completed Qty",
};

const ITEMS_PER_PAGE = 6; // set to 7 if preferred

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

const getCountValue = (itemCounts, key) =>
  itemCounts.find((c) => c.key === key)?.value ?? 0;

// ─────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────

const ProductionDetails = () => {
  const { orderId } = useParams();

  const { mode, managerId, designations } = useAuth();
  const { show } = useToast();
  const navigate = useNavigate();
  const productionSaveLock = useRef(false);
  // Local state only — no Redux for this screen
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acceptLoader, setAcceptLoader] = useState(false);
  const [productionLoader, setProductionLoader] = useState(false);
  const [maxQueue, setMaxQueue] = useState("");
  const [queueMaxLoader, setQueueMaxLoader] = useState(false);

  const [order, setOrder] = useState(null);
  const [itemCounts, setItemCounts] = useState([]);
  const [items, setItems] = useState([]);

  const [currentStatus, setCurrentStatus] = useState("New Voucher");

  const [selectedItem, setSelectedItem] = useState(null);
  const [queueEditItem, setQueueEditItem] = useState(null);
  const [queueloader, setQueueLoader] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [cancelItem, setCancelItem] = useState(null);
  const [cancelLoader, setCancelLoader] = useState(false);

  const [releaseLoader, setReleaseLoader] = useState(false);
  const [releaseLineItem, setReleaseLineItem] = useState(null);

  const [machines, setMachines] = useState([]);
  const [machine, setMachine] = useState("");
  const isSuperAdmin = designations.includes("superadmin");

  const [managerAuth, setManagerAuth] = useState({});

  useEffect(() => {
    const encrypted = localStorage.getItem("productionManagerAuth");

    if (encrypted) {
      try {
        // const bytes = CryptoJS.AES.decrypt(encrypted, "secret key");
        const decrypted = JSON.parse(CryptoDecrypt(encrypted));

        setManagerAuth(decrypted);
      } catch (error) {
        console.error("Failed to decrypt managerAuth:", error);
      }
    }
  }, []);

  // ── Status cards for ProductionFilters — all 6 itemCounts entries ──────
  const statusCards = useMemo(() => {
    const totalItems = getCountValue(itemCounts, "Total Items");

    return itemCounts.map((c) => ({
      status: KEY_TO_FILTER[c.key] || "All",
      label: c.key,
      count: c.value,
      allCount: totalItems,
      amount: c.value,
    }));
  }, [itemCounts]);

  // ── Items filtered by the selected status card ─────────────────────
  const filteredItems = useMemo(() => {
    if (selectedFilter === "All") return items;
    return items.filter((i) => i.status === selectedFilter);
  }, [items, selectedFilter]);

  // Reset to page 1 whenever the filter changes
  useEffect(() => {
    setPage(1);
  }, [selectedFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredItems.length / ITEMS_PER_PAGE),
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const paginatedItems = filteredItems.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  // Progress computed from live item state, falling back to voucher totals
  const totalRequired =
    items.reduce((s, i) => s + (i.requiredQty || 0), 0) || order?.totalQty || 0;
  const totalProduced =
    items.reduce((s, i) => s + (i.producedQty || 0), 0) ||
    order?.producedQty ||
    0;
  const progressPct =
    totalRequired > 0 ? Math.round((totalProduced / totalRequired) * 100) : 0;

  const getProductionVoucher = async () => {
    if (!orderId) return;

    const apiData = {
      managerId,
      voucherId: orderId,
    };

    try {
      setLoading(true);
      setError(null);

      const result = await fetchData("production/productionVoucher", apiData);

      if (result?.status === "success") {
        const voucher = result?.data?.[0];

        if (voucher) {
          setOrder(voucher);
          setItemCounts(result.itemCounts || []);
          setCurrentStatus(voucher.status || "New Voucher");

          const mappedItems = (voucher.items || [])
            .slice()
            // .sort((a, b) => (a.itemQueue || 0) - (b.itemQueue || 0))
            .map((it) => ({
              id: it.itemId,
              voucherId: voucher?.voucherId,
              orderId: voucher?.voucherId,
              voucherQueue: voucher.voucherQueue,
              queueNo: it.itemQueue,
              itemCode: it.itemNo,
              productName: it.product_name,
              collectionName: it.collection_name,
              requiredQty: it.qty,
              producedQty: it.producedQty,
              pendingQty: it.pendingQty,
              warehouse: voucher.warehousename,
              // machine: it.pLName || it.productionLine || "",
              machine: it.pLName || "",
              productionLine: it.productionLine || "",
              pLId: it.productionLine || "",
              status: it.status,
              updatedAt: voucher.lastAction,
              thumbnail: it.thumbnail,
              unit: it.unit,
              price: it.price,
              itemsHistory: voucher?.itemsHistory || [],
              voucherStatus: voucher?.status,
            }));
          if (result.productionLines) {
            setMachines(
              result.productionLines.map((p) => ({
                label: p.name,
                value: p.pLId,
              })),
            );
          }

          setItems(mappedItems);
        } else {
          setItems([]);
          setItemCounts([]);
        }
      } else {
        setError(result?.msg || "Failed to fetch voucher details.");
        show(result?.msg || "Failed to fetch voucher details.", "error");
      }
    } catch (err) {
      console.error("Voucher fetching error:", err);
      setError("Failed to fetch voucher.");
      show("Failed to fetch voucher.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProductionVoucher();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, managerId]);

  const handleSave = async ({ item, qty }) => {
    if (productionSaveLock.current) return;

    productionSaveLock.current = true;

    try {
      setProductionLoader(true);

      const apiData = {
        managerId,
        voucherId: item?.voucherId || orderId,
        itemId: item?.id,
        producedQty: qty,
        productionLine: machine || "",
      };

      // console.log("updateItemWiseProduction payload:", apiData);

      const result = await fetchData(
        "production/updateItemWiseProduction",
        apiData,
      );

      if (result?.status === "success") {
        show(
          result?.msg || "Production quantity updated successfully.",
          "success",
        );

        setSelectedItem(null);

        await getProductionVoucher();
      } else {
        show(result?.msg || "Failed to update production quantity.", "error");
      }
    } catch (error) {
      console.error("Item Wise production update error:", error);

      show("Failed to update production quantity.", "error");
    } finally {
      setProductionLoader(false);
      productionSaveLock.current = false;
    }
  };

  const handleOverSave = async ({ item, qty }) => {
    if (productionSaveLock.current) return;

    productionSaveLock.current = true;

    try {
      setProductionLoader(true);

      const apiData = {
        managerId,
        voucherId: item?.voucherId || orderId,
        itemId: item?.id,
        producedQty: qty,
        productionLine: machine || "",
      };

      // console.log("updateOverProduceQty payload:", apiData);

      const result = await fetchData(
        "production/updateOverProduceQty",
        apiData,
      );

      if (result?.status === "success") {
        show(
          result?.msg || "Production quantity updated successfully.",
          "success",
        );

        setSelectedItem(null);

        await getProductionVoucher();
      } else {
        show(result?.msg || "Failed to update production quantity.", "error");
      }
    } catch (error) {
      console.error("Item Wise production update error:", error);

      show("Failed to update production quantity.", "error");
    } finally {
      setProductionLoader(false);
      productionSaveLock.current = false;
    }
  };

  const openUpdateModal = (item) => {
    setSelectedItem(item);
    setMachine(item.pLId || "");
  };

  const openCancelOrderModal = (item) => {
    setCancelItem(item);
  };

  const openReleaseLineModal = (item) => {
    setReleaseLineItem(item);
  };

  const handleCancelOrder = async () => {
    if (!cancelItem) return;

    let endpoint;
    let apiData;

    endpoint = "production/cancelProductionItem";

    apiData = {
      managerId,
      voucherId: cancelItem?.orderId,
      itemId: cancelItem?.itemId || cancelItem?.id,
    };

    try {
      setCancelLoader(true);

      const result = await fetchData(endpoint, apiData);

      if (result?.status === "success") {
        show(result?.msg || "Item cancelled successfully.", "success");

        setCancelItem(null);
        await getProductionVoucher();
      } else {
        show(result?.msg || "Failed to cancel item.", "error");
      }
    } catch (error) {
      console.error("Cancel production error:", error);

      show("Failed to cancel item.", "error");
    } finally {
      setCancelLoader(false);
    }
  };

  const handleReleaseLine = async (item) => {
    const apiData = {
      managerId,
      voucherId: item?.orderId,
      itemId: item?.id,
    };

    try {
      setReleaseLoader(true);

      const result = await fetchData(
        "production/releaseProductionItem",
        apiData,
      );

      // console.log("Priority update result:", result);

      if (result?.status === "success") {
        show(result?.msg || "Line Released successfully.", "success");

        setReleaseLineItem(null);
        await getProductionVoucher();
      } else {
        show(result?.msg || "Failed to Line Release.", "error");
      }
    } catch (error) {
      console.error("Line Release error:", error);
      show("Failed to Line Release.", "error");
    } finally {
      setReleaseLoader(false);
    }
  };

  const currentOrder = {
    orderId: order?.voucherId || orderId,
    customerName: order?.name || "",
    companyName: order?.storeName || "",
    priority: order?.priority || "",
    createdAt: order?.createdAt,
    createdBy: order?.createdBy,
    orderDate: order?.voucherDate,
    warehousename: order?.warehousename,
    shippingAddress: order?.shippingAddress,
    whatsapp: order?.whatsapp,
    email: order?.email,
    gstuin: order?.gstuin,
  };

  const isNewVoucher = currentStatus === "New Voucher";
  const inProduction = currentStatus === "In Production";
  const readyForDispatch = currentStatus === "Ready For Dispatch" || currentStatus === "Dispatched";
  const isCancelled = currentStatus === "Cancelled";
  const getStatusColor = () => {
    if (isNewVoucher) return "#4164e3";
    if (inProduction) return "#f79009";
    if (readyForDispatch) return "#12b76a";
    if (isCancelled) return "#b71d12";
    return "#646b88";
  };

  const openModal = (content) => {
    setIsModalOpen(true);
    setModalContent(content);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalContent(null);
  };

  const openAcceptOrderModal = () => {
    openModal(
      <AcceptProductionModal
        heading={"Accept Production Voucher"}
        centerHeading={
          "Are you sure you want to accept this Production Voucher?"
        }
        targetWord={"Production Voucher"}
        handleSubmit={acceptVoucher}
        handleCloseModal={closeModal}
      />,
    );
  };

  const acceptVoucher = async () => {
    const apiData = {
      managerId,
      voucherId: orderId,
    };
    try {
      setAcceptLoader(true);

      const result = await fetchData("production/acceptProduction", apiData);

      if (result?.status === "success") {
        show(result?.msg || "Voucher accepted successfully.", "success");
        getProductionVoucher();
        closeModal();
      } else {
        show(result?.msg || "Failed to accept voucher.", "error");
      }
    } catch (error) {
      console.error("Voucher accept error:", error);
      show("Failed to accept voucher.", "error");
    } finally {
      setAcceptLoader(false);
    }
  };

  const handleQueueEdit = async (item) => {
    // console.log(item);

    setQueueEditItem(item);

    let endpoint;
    let payload;

    endpoint = "production/getMaxItemQueue";

    payload = {
      managerId,
      voucherId: item?.orderId || item?.voucherNo,
    };

    try {
      setQueueMaxLoader(true);

      // console.log("Queue API:", endpoint);
      // console.log("Queue Payload:", payload);

      const result = await fetchData(endpoint, payload);

      if (result?.status === "success") {
        setMaxQueue(result?.msg);
      } else {
        show(result?.msg || "Failed to get maxQueue", "error");
        setMaxQueue(0);
      }
    } catch (error) {
      console.error("Get max queue error:", error);
      setMaxQueue(0);
      show("Failed to get maxQueue", "error");
    } finally {
      setQueueMaxLoader(false);
    }
  };

  const handleQueueUpdate = async (v, n, itemId) => {
    // console.log("Queue Update:", v, n, itemId);
    // console.log("Active Type:", activeType);

    let endpoint;
    let apiData;

    endpoint = "production/updateItemQueue";

    apiData = {
      managerId,
      voucherId: v,
      itemId: itemId,
      newItemQueue: n,
    };

    try {
      setQueueLoader(true);

      const result = await fetchData(endpoint, apiData);

      // console.log("Queue update result:", result);

      if (result?.status === "success") {
        show(result?.msg || "Queue updated successfully.", "success");

        setQueueEditItem(null);
        getProductionVoucher();
      } else {
        show(result?.msg || "Failed to update queue.", "error");
      }
    } catch (error) {
      console.error("Queue update error:", error);

      show("Failed to update queue.", "error");
    } finally {
      setQueueLoader(false);
    }
  };

  // ── Full-screen loader while the voucher is being fetched ────────────
  if (loading) {
    return (
      <div
        className={`${styles.production} ${mode ? styles.production_dark : ""}`}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: "12px",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: "4px solid #d0d5dd",
            borderTopColor: "#4164e3",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <span style={{ color: mode ? "#fff" : "#646B88", fontSize: 14 }}>
          Loading production voucher...
        </span>
        <style>
          {`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
        </style>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────
  if (error && !order) {
    return (
      <div
        className={`${styles.production} ${mode ? styles.production_dark : ""}`}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: "12px",
        }}
      >
        <span style={{ color: "#f04438", fontSize: 14 }}>{error}</span>
        <Button
          buttonText={"Retry"}
          buttonBgColor={"#4164E3"}
          buttonTextColor={"#ffffff"}
          buttonClick={getProductionVoucher}
        />
      </div>
    );
  }

  return (
    <div
      className={`${styles.production} ${mode ? styles.production_dark : ""}`}
      style={{ paddingBottom: "80px" }}
    >
      <ModalWrapper
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        noBtn
      >
        {modalContent}
      </ModalWrapper>

      {cancelItem && (
        <ModalWrapper
          isOpen={!!cancelItem}
          onClose={cancelLoader ? undefined : () => setCancelItem(null)}
        >
          <CancelReasonModal
            onClose={() => setCancelItem(null)}
            onSubmit={handleCancelOrder}
            mode={mode}
            type={"Voucher Item"}
            heading={"Cancel Voucher Item"}
            subHeading={`Are you sure you want to cancel ${`${cancelItem?.orderId || ""} - ${cancelItem?.itemCode || ""} `}?`}
            loading={cancelLoader}
            activeType={"Item Wise"}
          />
        </ModalWrapper>
      )}

      {releaseLineItem && (
        <ReleaseLineModal
          item={releaseLineItem}
          mode={mode}
          activeType={"Item Wise"}
          releaseLoader={releaseLoader}
          onConfirm={handleReleaseLine}
          onClose={() => setReleaseLineItem(null)}
        />
      )}

      <div className={styles.production_top}>
        <div className={styles.production_top_left}>
          <TopTabBar
            text={`Production Voucher / Order Wise / ${currentOrder.orderId}`}
          />
        </div>

        <div className={styles.production_right}>
          <TextElement
            label={"Voucher No. & Date:"}
            labelColor={mode ? "#fff" : "#646B88"}
            value={
              currentOrder.orderId +
              " / " +
              formatDateTime(currentOrder.orderDate)
            }
            valueColor={mode ? "#fff" : "#646B88"}
            fontSize={"12px"}
            type={"order"}
          />
          <TextElement
            label={"Created By:"}
            labelColor={mode ? "#fff" : "#646B88"}
            value={currentOrder.createdBy}
            valueColor={mode ? "#fff" : "#646B88"}
            fontSize={"12px"}
            type={"order"}
          />

          <TextElement
            label={"Status:"}
            labelColor={mode ? "#fff" : "#646B88"}
            value={currentStatus}
            valueColor={getStatusColor()}
            fontSize={"12px"}
            type={"order"}
          />
        </div>
      </div>

      {/* Header Card */}
      <div
        className={`${styles.production_header} ${
          mode ? styles.production_header_dark : ""
        }`}
      >
        <div>
          <p>
            Customer: {currentOrder.customerName} / {currentOrder.companyName}
          </p>
          <p>
            Address:{" "}
            {currentOrder &&
              currentOrder?.shippingAddress?.street +
                ", " +
                currentOrder?.shippingAddress?.city +
                ", " +
                currentOrder?.shippingAddress?.state +
                ", " +
                currentOrder?.shippingAddress?.pincode}
          </p>

          <p>Contact: {currentOrder.whatsapp}</p>
          <p>Email: {currentOrder.email}</p>
          <p>GSTUIN : {currentOrder.gstuin}</p>
          <p>Warehouse: {currentOrder.warehousename}</p>
        </div>

        <div
          className={styles.urgentBadge}
          style={{
            backgroundColor:
              currentOrder.priority === "Normal" ? "#4164E3" : undefined,
          }}
        >
          {currentOrder.priority}
        </div>
      </div>

      {/* Status Filter Cards — your ProductionFilters component */}
      <div
        className={styles.production_filters}
        style={
          mode ? { borderColor: "#232529", backgroundColor: "#232529" } : {}
        }
      >
        <ProductionFilters
          mode={mode}
          headerData={statusCards}
          selectedFilter={selectedFilter}
          handleClick={() => {}}
          // getData={getProductionVoucher}
          type={"production"}
        />
      </div>

      {/* Progress */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <div className={styles.progressCard}>
          <div className={styles.progressHeader}>
            <span>Production Progress</span>
            <span>{progressPct}%</span>
          </div>
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
        {/* ── Pagination — same component/UI used on the Production list page ── */}
        <div className={styles.pegination}>
          <Pagination2
            mode={mode}
            currentPage={page}
            setCurrentPage={(newPage) => setPage(newPage)}
            totalPages={totalPages}
          />
        </div>
      </div>

      {/* Table */}
      <ul
        className={styles.productionTable}
        style={{ gridTemplateColumns: GRID_COLUMNS }}
      >
        <li
          className={styles.productionTableHeader}
          style={{ gridTemplateColumns: GRID_COLUMNS }}
        >
          {TABLE_HEADERS.map((header, index) => (
            <div title={header} key={index}>
              {header}
            </div>
          ))}
        </li>

        {paginatedItems.length === 0 && (
          <li style={{ padding: "16px", textAlign: "center" }}>
            No items found.
          </li>
        )}

        {paginatedItems.map((item) => (
          <li
            key={item.id}
            className={`${styles.productionTableRow} ${
              mode ? styles.productionTableRowDark : ""
            }`}
            style={{ gridTemplateColumns: GRID_COLUMNS }}
          >
            <div title={item.queueNo || item?.voucherQueue || ""}>
              {item?.voucherQueue && item.queueNo
                ? `${item?.voucherQueue}/${item.queueNo}`
                : ""}
            </div>
            <div title={item.itemCode}>{item.itemCode}</div>
            <div title={item.productName}>{item.productName}</div>
            <div title={item.requiredQty}>{item.requiredQty}</div>
            <div
              title={item.producedQty}
              style={{ color: "#12b76a", fontWeight: 600 }}
            >
              {item.producedQty}
            </div>
            <div
              title={item.pendingQty}
              style={{
                color: item.pendingQty > 0 ? "#f04438" : "#12b76a",
                fontWeight: 600,
              }}
            >
              {item.pendingQty}
            </div>
            <div title={item.warehouse}>{item.warehouse}</div>
            <div title={item.machine}>{item.machine}</div>

            <div style={{ width: "80%", paddingRight: 4 }}>
              <ProgressBar
                produced={item.producedQty}
                required={item.requiredQty || 1}
                mode={mode}
              />
            </div>

            <div title={item.status}>
              {item.status === "Completed" ? (
                <span className={styles.completed}>Completed</span>
              ) : item.status === "In Production" ? (
                <span className={styles.inProduction}>In Production</span>
              ) : (
                <span className={styles.pending}>{item.status}</span>
              )}
            </div>

            <div title={item.updatedAt}>
              <LastActionCell lastAction={item.updatedAt} />
            </div>
            {item?.voucherStatus !== "Cancelled" &&
              (!item?.machine?.trim() ||
                machines.some(
                  (machine) =>
                    machine?.label?.trim().toLowerCase() ===
                    item?.machine?.trim().toLowerCase(),
                )) && (
                <div>
                  <ThreeDotMenu
                    item={item}
                    mode={mode}
                    onUpdate={() => openUpdateModal(item)}
                    onRelease={() => openReleaseLineModal(item)}
                    // onQueueEdit={() => setQueueEditItem(item)}
                    onQueueEdit={handleQueueEdit}
                    // onCancel={() => handleCancelItem(item)}
                    onCancel={(it) => openCancelOrderModal(it)}
                    activeType={"Item Wise"}
                    managerAuth={managerAuth}
                    isSuperAdmin={isSuperAdmin}
                  />
                </div>
              )}
          </li>
        ))}
      </ul>

      <footer className={`${styles.footer} ${mode ? styles.footer_dark : ""}`}>
        <div>
          <TextElement
            label={"Total Items:"}
            labelColor={mode ? "#fff" : "#646B88"}
            value={filteredItems?.length}
            valueColor={mode ? "#fff" : "#646B88"}
            fontSize={"14px"}
            fontWeight={"400"}
          />
        </div>

        <div className={styles.btn_wrapper}>
          <div>
            <Button
              buttonIcon={<LeftArrow color={"#FFFFFF"} />}
              buttonText={"Back"}
              buttonBgColor={"#000000"}
              buttonTextColor={"#ffffff"}
              buttonClick={() => navigate(-1)}
            />
          </div>

          {isNewVoucher && (
            <div>
              <Button
                buttonIcon={<CancelIcon2 />}
                buttonText={"Cancel"}
                buttonBgColor={"#000000"}
                buttonTextColor={"#ffffff"}
              />
            </div>
          )}

          {isNewVoucher && (
            <div>
              <Button
                buttonIcon={<TemplateDone />}
                buttonText={"Accept"}
                buttonBgColor={"#4164E3"}
                buttonTextColor={"#ffffff"}
                buttonClick={openAcceptOrderModal}
              />
            </div>
          )}
        </div>
      </footer>

      {selectedItem && (
        <UpdateDrawer
          setItems={setItems}
          item={selectedItem}
          mode={mode}
          show={show}
          onClose={() => setSelectedItem(null)}
          onSave={handleSave}
          selectedItem={selectedItem}
          activeTab={"Order Wise"}
          productionLoader={productionLoader}
          machine={machine}
          machineOptions={machines}
          setMachine={setMachine}
          productionSaveLock={productionSaveLock}
          onOverSave={handleOverSave}
        />
      )}

      {queueEditItem && (
        <QueueEditModal
          item={queueEditItem}
          maxQueue={maxQueue}
          mode={mode}
          queueloader={queueloader}
          queueMaxLoader={queueMaxLoader}
          onConfirm={(n) =>
            handleQueueUpdate(queueEditItem.voucherId, n, queueEditItem?.id)
          }
          onClose={() => {
            setQueueEditItem(null);
            setMaxQueue(0);
          }}
          activeType={"Item Wise"}
        />
      )}
    </div>
  );
};

export default ProductionDetails;
